import { useEffect, useRef, useState } from "react";
import ApiClass from "../api";
import persistedState from "../persited_store";
import utilityfun from "../helpers/utilityfun";
import { WritingReply } from "./WritingReply";
import { parseStructuredReply } from "./ChatStructuredReply";
import "../assets/css/askLoading.css";

const compactQuestionForApi = (question) => {
  if (!question || typeof question !== "object") return question;
  const imageUrl = String(question.imageUrl || "");
  const next = { ...question };
  if (imageUrl.startsWith("data:")) delete next.imageUrl;
  if (String(next.imagePrompt || "").length > 500) {
    next.imagePrompt = String(next.imagePrompt).slice(0, 500);
  }
  delete next.altText;
  delete next.generatingVisual;
  return next;
};

const referenceIdsFromReply = (parsed) => {
  if (!parsed || typeof parsed !== "object") return null;
  const topics = [];
  const addTopic = (topic, strand) => {
    const topicName = String(topic?.topicName || topic?.name || "").trim();
    const topicId = Number(topic?.topicId || topic?.id) || 0;
    if (!topicName && !topicId) return;
    topics.push({
      topicId,
      topicName,
      strandId: Number(topic?.strandId || strand?.strandId || strand?.id) || 0,
      strand: String(topic?.strand || strand?.strand || strand?.name || "").trim(),
    });
  };
  (Array.isArray(parsed.strands) ? parsed.strands : []).forEach((strand) => {
    (Array.isArray(strand?.topics) ? strand.topics : []).forEach((topic) => addTopic(topic, strand));
  });
  (Array.isArray(parsed.topics) ? parsed.topics : []).forEach((topic) => addTopic(topic, parsed));
  const refs = {
    gradeId: Number(parsed.gradeId) || 0,
    grade: String(parsed.grade || ""),
    subjectGradeId: Number(parsed.subjectGradeId) || 0,
    subject: String(parsed.subject || ""),
    strandId: Number(parsed.strandId) || 0,
    strand: String(parsed.strand || ""),
    topicId: Number(parsed.topicId) || 0,
    topicName: String(parsed.topicName || ""),
    topics,
  };
  const hasIds =
    refs.gradeId ||
    refs.subjectGradeId ||
    refs.strandId ||
    refs.topicId ||
    refs.subject ||
    refs.topics.length;
  return hasIds ? refs : null;
};

const compactAskHistory = (rows) =>
  rows
    .filter((item) => item.from === "user" || item.from === "bot")
    .slice(-20)
    .map((item) => {
      const role = item.from === "bot" ? "assistant" : "user";
      const raw = String(item.text || "");
      if (role === "user") return { role, content: raw.slice(0, 4000) };
      const parsed = parseStructuredReply(raw);
      if (!parsed || typeof parsed !== "object") {
        return { role, content: raw.slice(0, 4000) };
      }
      const compact = { ...parsed };
      if (Array.isArray(compact.questions)) {
        compact.questions = compact.questions.map(compactQuestionForApi);
      }
      if (String(compact.imageUrl || "").startsWith("data:")) delete compact.imageUrl;
      delete compact.visualPrompt;
      const refs = referenceIdsFromReply(compact);
      return {
        role,
        content: JSON.stringify(compact).slice(0, 24000),
        ...(refs ? { refs } : {}),
      };
    });

const AGENTS = [
  { slug: "standard", name: "Standard Agent" },
  { slug: "question", name: "Question Agent" },
  { slug: "image", name: "Image Generate Agent" },
];

function loggedInName() {
  const user = persistedState.user();
  if (!user) return "";
  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.fullName ||
    user.userName ||
    ""
  );
}

const isAdminUser = () =>
  utilityfun.isAdmin() || utilityfun.isDistrictAdmin() || utilityfun.isSchoolAdmin();

const GeneratingStatus = () => (
  <div className="ask-loading-bubble" aria-label="Writing">
    <div className="ask-loading-dots">
      <span />
      <span />
      <span />
    </div>
  </div>
);

export function ChatBot({ bottom = 16 }) {
  const showAskAi = isAdminUser();
  const [open, setOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("default");
  const [input, setInput] = useState("");
  const [askInput, setAskInput] = useState("");
  const [chatId, setChatId] = useState(null);
  const [askChatId, setAskChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [askLoading, setAskLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [askMessages, setAskMessages] = useState([]);
  const [agentSlug, setAgentSlug] = useState("question");
  const [askHistoryOpen, setAskHistoryOpen] = useState(false);
  const [askThreads, setAskThreads] = useState([]);
  const [askThreadsLoading, setAskThreadsLoading] = useState(false);
  const endRef = useRef(null);
  const askEndRef = useRef(null);
  const askListRef = useRef(null);
  const askStickToBottom = useRef(true);
  const chatListRef = useRef(null);
  const chatStickToBottom = useRef(true);
  const inputRef = useRef(null);
  const askInputRef = useRef(null);
  const startingRef = useRef(false);

  const focusInput = (ref) => {
    requestAnimationFrame(() => ref.current?.focus());
  };

  const startChat = async () => {
    if (startingRef.current || chatId) return;
    startingRef.current = true;
    setLoading(true);
    try {
      const body = {};
      const name = loggedInName();
      if (name) body.name = name;
      const res = await ApiClass.postNodeRequest(
        "chatbot/start",
        !!persistedState.token(),
        body
      );
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        if (data?.chatId) setChatId(data.chatId);
        if (data?.reply) {
          setMessages((m) => [...m, { from: "bot", text: data.reply }]);
        }
      } else {
        startingRef.current = false;
        setMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      startingRef.current = false;
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setLoading(false);
      focusInput(inputRef);
    }
  };

  useEffect(() => {
    if (!open) return;
    focusInput(inputRef);
    startChat();
  }, [open]);

  useEffect(() => {
    if (!askOpen) return;
    if (!askMessages.length) {
      const agent = AGENTS.find((item) => item.slug === agentSlug);
      setAskMessages([
        {
          from: "bot",
          text: `Ask AI is using ${agent?.name || "the selected agent"} with its configured prompt, skills, and knowledge.`,
          instant: true,
        },
      ]);
    }
    focusInput(askInputRef);
  }, [askOpen]);

  useEffect(() => {
    if (!chatStickToBottom.current) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (!askStickToBottom.current) return;
    askEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [askMessages, askOpen]);

  const onListScroll = (event, stickRef) => {
    const el = event.currentTarget;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
  };

  useEffect(() => {
    const isChatWindow = (el) => {
      const w = parseInt(el?.style?.width || "0", 10);
      const h = parseInt(el?.style?.height || "0", 10);
      return w > 280 || h > 90;
    };

    const hideNativeLauncher = () => {
      try {
        window.$zopim?.livechat?.button?.hide?.();
      } catch {
        /* ignore */
      }
      document
        .querySelectorAll(
          'iframe#launcher, iframe[title="Button to launch messaging window"]'
        )
        .forEach((el) => el.style.setProperty("display", "none", "important"));
      document.querySelectorAll(".zopim").forEach((el) => {
        if (!isChatWindow(el)) el.style.setProperty("display", "none", "important");
      });
    };

    hideNativeLauncher();
    const retries = [300, 800, 1600, 3200, 6000].map((ms) =>
      setTimeout(hideNativeLauncher, ms)
    );
    const observer = new MutationObserver(hideNativeLauncher);
    observer.observe(document.body, { childList: true, subtree: true });
    if (window.$zopim) {
      window.$zopim(hideNativeLauncher);
    }
    return () => {
      retries.forEach(clearTimeout);
      observer.disconnect();
    };
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    chatStickToBottom.current = true;

    setInput("");
    setMessages((m) => [...m, { from: "user", text }]);
    setLoading(true);
    focusInput(inputRef);

    try {
      const body = { message: text, chatId };
      if (!chatId) {
        const name = loggedInName();
        if (name) body.name = name;
      }
      const res = await ApiClass.postNodeRequest(
        "chatbot/ask",
        !!persistedState.token(),
        body
      );
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        if (data?.chatId) setChatId(data.chatId);
        setMessages((m) => [...m, { from: "bot", text: data?.reply || "" }]);
      } else {
        setMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setLoading(false);
      focusInput(inputRef);
    }
  };

  const sendAskAiMessage = async (text) => {
    if (!text || askLoading) return;

    const history = compactAskHistory(askMessages);
    const botIndex = askMessages.length + 1;
    askStickToBottom.current = true;

    setAskInput("");
    setAskMessages((m) => [...m, { from: "user", text }]);
    setAskLoading(true);
    focusInput(askInputRef);

    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai", true, {
        message: text,
        agentSlug,
        history,
        chatId: askChatId,
      });
      const { status_code, data, message } = res?.data || {};
      if (status_code == 1) {
        if (data?.chatId) setAskChatId(data.chatId);
        const replyText = data?.reply || "";
        setAskMessages((m) => [
          ...m,
          {
            from: "bot",
            text: replyText,
            messageId: data?.messageId || null,
            feedback: null,
          },
        ]);
        const parsed = parseStructuredReply(replyText);
        const targets = Array.isArray(parsed?.visualIndexes)
          ? parsed.visualIndexes
              .map((value) => Number(value) - 1)
              .filter((index) => Number.isInteger(index) && index >= 0)
          : [];
        if (parsed?.autoAddVisuals && Array.isArray(parsed?.questions) && targets.length) {
          setAskLoading(false);
          await addAskVisuals(botIndex, parsed, targets);
          return;
        }
      } else {
        setAskMessages((m) => [
          ...m,
          { from: "bot", text: message || "Something went wrong." },
        ]);
      }
    } catch {
      setAskMessages((m) => [
        ...m,
        { from: "bot", text: "Could not reach the server." },
      ]);
    } finally {
      setAskLoading(false);
      focusInput(askInputRef);
    }
  };

  const patchAskMessage = (index, updater) => {
    setAskMessages((rows) =>
      rows.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const parsed = parseStructuredReply(row.text);
        if (!parsed || typeof parsed !== "object") return row;
        return { ...row, text: JSON.stringify(updater(parsed)), instant: true };
      })
    );
  };

  const saveAskStandards = async (index, data) => {
    if (askLoading) return;
    patchAskMessage(index, (parsed) => ({ ...parsed, saving: true }));
    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai/save-standards", true, data);
      const { status_code, data: payload, message } = res?.data || {};
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: false,
        saving: false,
        savedStandards:
          payload?.savedStandards || {
            ok: status_code == 1,
            message: message || "Unable to save standards.",
          },
      }));
    } catch {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        saving: false,
        savedStandards: { ok: false, message: "Unable to save standards." },
      }));
    }
  };

  const cancelAskStandards = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      pendingSave: false,
      saving: false,
      discarded: true,
    }));
  };

  const saveAskQuestions = async (index, data) => {
    if (askLoading) return;
    if (data?.savedQuestions?.ok) {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: false,
        saving: false,
        savedQuestions: {
          ok: true,
          message:
            parsed.savedQuestions?.message ||
            "These questions were already saved. No duplicate entries created.",
        },
      }));
      return;
    }
    patchAskMessage(index, (parsed) => ({ ...parsed, saving: true }));
    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai/save-questions", true, {
        ...data,
        questions: (data.questions || []).map(compactQuestionForApi),
        saveFingerprint: data.saveFingerprint || undefined,
      });
      const { status_code, data: payload, message } = res?.data || {};
      const saved =
        payload?.savedQuestions || {
          ok: status_code == 1,
          message: message || "Unable to save questions.",
        };
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        pendingSave: saved.ok ? false : true,
        saving: false,
        savedQuestions: saved,
      }));
    } catch {
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        saving: false,
        savedQuestions: { ok: false, message: "Unable to save questions." },
      }));
    }
  };

  const cancelAskQuestions = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      pendingSave: false,
      pendingVisuals: false,
      saving: false,
      discarded: true,
    }));
  };

  const setAskFeedback = async (index, nextFeedback) => {
    const row = askMessages[index];
    if (!row || row.from !== "bot" || !row.messageId) return;
    if (row.feedbackNoted || row.feedback) return;
    const feedback = nextFeedback === "like" || nextFeedback === "unlike" ? nextFeedback : null;
    if (!feedback) return;
    setAskMessages((rows) =>
      rows.map((item, itemIndex) =>
        itemIndex === index ? { ...item, feedback, feedbackNoted: true } : item
      )
    );
    try {
      const res = await ApiClass.postNodeRequest("admin/ask-ai/feedback", true, {
        messageId: row.messageId,
        feedback,
      });
      const { status_code, data, message } = res?.data || {};
      if (status_code != 1) {
        setAskMessages((rows) =>
          rows.map((item, itemIndex) =>
            itemIndex === index ? { ...item, feedback: null, feedbackNoted: false } : item
          )
        );
        console.log(message || "Unable to save feedback.");
      } else if (data?.feedback) {
        setAskMessages((rows) =>
          rows.map((item, itemIndex) =>
            itemIndex === index
              ? { ...item, feedback: data.feedback, feedbackNoted: true }
              : item
          )
        );
      }
    } catch {
      setAskMessages((rows) =>
        rows.map((item, itemIndex) =>
          itemIndex === index ? { ...item, feedback: null, feedbackNoted: false } : item
        )
      );
    }
  };

  const skipAskVisuals = (index) => {
    patchAskMessage(index, (parsed) => ({
      ...parsed,
      pendingVisuals: false,
      addingVisuals: false,
      visualsSkipped: true,
    }));
  };

  const addAskVisuals = async (index, data, preferredIndexes) => {
    if (askLoading || data.addingVisuals) return;
    const requested = Array.isArray(preferredIndexes) ? preferredIndexes : [];
    const defaults = (data.questions || [])
      .map((question, questionIndex) =>
        question.needsImage && !question.imageUrl && !question.questionImgName ? questionIndex : -1
      )
      .filter((questionIndex) => questionIndex >= 0);
    const targets = (requested.length ? requested : defaults)
      .filter((questionIndex) => questionIndex >= 0 && questionIndex < (data.questions || []).length)
      .slice(0, 12);
    if (!targets.length) {
      patchAskMessage(index, (parsed) => ({ ...parsed, pendingVisuals: false, addingVisuals: false }));
      return;
    }

    const history = compactAskHistory(askMessages);
    let working = (data.questions || []).map((question) => ({ ...question, generatingVisual: false }));

    for (let step = 0; step < targets.length; step += 1) {
      const questionIndex = targets[step];
      working = working.map((question, itemIndex) => ({
        ...question,
        generatingVisual: itemIndex === questionIndex,
      }));
      patchAskMessage(index, (parsed) => ({
        ...parsed,
        addingVisuals: true,
        pendingVisuals: true,
        visualError: "",
        visualProgress: `Generating image ${step + 1} of ${targets.length} (Question ${questionIndex + 1})…`,
        questions: working,
      }));
      try {
        const res = await ApiClass.postNodeRequest("admin/ask-ai", true, {
          message: "Add visuals to the required questions",
          agentSlug: "question",
          chatId: askChatId,
          addVisuals: true,
          visualIndex: questionIndex,
          questions: working.map(compactQuestionForApi),
          topicId: data.topicId,
          topicName: data.topicName,
          grade: data.grade,
          subject: data.subject,
          strand: data.strand,
          history,
        });
        const { status_code, data: payload, message } = res?.data || {};
        if (payload?.chatId) setAskChatId(payload.chatId);
        const parsedReply = status_code == 1 ? parseStructuredReply(payload?.reply) : null;
        if (parsedReply?.questions?.[questionIndex]) {
          working = parsedReply.questions.map((question, itemIndex) => ({
            ...question,
            generatingVisual: false,
            imageUrl: question.imageUrl || working[itemIndex]?.imageUrl,
            questionImgName: question.questionImgName || working[itemIndex]?.questionImgName,
          }));
        } else {
          working = working.map((question, itemIndex) =>
            itemIndex === questionIndex
              ? { ...question, generatingVisual: false, imageError: message || "Unable to add this visual." }
              : { ...question, generatingVisual: false }
          );
        }
        patchAskMessage(index, (parsed) => ({
          ...parsed,
          ...parsedReply,
          questions: working,
          addingVisuals: step < targets.length - 1,
          pendingVisuals: true,
          pendingSave: parsedReply?.pendingSave === true || (parsedReply?.pendingSave == null && parsed.pendingSave === true),
          visualProgress: `Generated image ${step + 1} of ${targets.length}.`,
        }));
      } catch {
        working = working.map((question, itemIndex) =>
          itemIndex === questionIndex
            ? { ...question, generatingVisual: false, imageError: "Unable to add this visual." }
            : { ...question, generatingVisual: false }
        );
        patchAskMessage(index, (parsed) => ({
          ...parsed,
          questions: working,
          addingVisuals: step < targets.length - 1,
          visualError: "Unable to add this visual.",
        }));
      }
    }

    patchAskMessage(index, (parsed) => ({
      ...parsed,
      questions: working.map((question) => ({ ...question, generatingVisual: false })),
      addingVisuals: false,
      pendingVisuals: false,
      visualProgress: "",
      visualPrompt: "",
    }));
    focusInput(askInputRef);
  };

  const sendAskAi = (e) => {
    e.preventDefault();
    sendAskAiMessage(askInput.trim());
  };

  const openHelp = () => {
    try {
      if (window.$zopim?.livechat?.window?.show) {
        window.$zopim.livechat.window.show();
        return;
      }
    } catch {
      /* ignore */
    }
    try {
      window.zE?.("webWidget", "open");
    } catch {
      /* ignore */
    }
    try {
      window.zE?.("messenger", "open");
    } catch {
      /* ignore */
    }
  };

  const introForAgent = (slug, switched) => {
    const agent = AGENTS.find((item) => item.slug === slug);
    const name = agent?.name || "the selected agent";
    return switched
      ? `Switched to ${name}. Ask using this agent's prompt, skills, and knowledge.`
      : `Ask AI is using ${name} with its configured prompt, skills, and knowledge.`;
  };

  const startNewAskThread = () => {
    setAskHistoryOpen(false);
    setAskChatId(null);
    setAskMessages([
      {
        from: "bot",
        text: introForAgent(agentSlug, false),
        instant: true,
      },
    ]);
    focusInput(askInputRef);
  };

  const openAskHistory = async () => {
    setAskHistoryOpen(true);
    setAskThreadsLoading(true);
    try {
      const res = await ApiClass.getNodeRequest(
        `admin/ask-ai/threads?agentSlug=${encodeURIComponent(agentSlug)}`
      );
      const rows = res?.data?.status_code == 1 && Array.isArray(res.data.data) ? res.data.data : [];
      setAskThreads(rows);
    } catch {
      setAskThreads([]);
    } finally {
      setAskThreadsLoading(false);
    }
  };

  const openAskThread = async (chatId) => {
    if (!chatId || askThreadsLoading) return;
    setAskThreadsLoading(true);
    try {
      const res = await ApiClass.getNodeRequest(
        `admin/ask-ai/threads/${encodeURIComponent(chatId)}`
      );
      const thread = res?.data?.status_code == 1 ? res.data.data : null;
      if (!thread?.chatId) return;
      const slug = thread.agentSlug || agentSlug;
      setAgentSlug(slug);
      setAskChatId(thread.chatId);
      const restored = (thread.messages || []).map((item) => ({
        from: item.role === "assistant" ? "bot" : "user",
        text: item.content || "",
        messageId: item.role === "assistant" ? item.messageId : null,
        feedback: item.feedback || null,
        instant: true,
      }));
      setAskMessages(
        restored.length
          ? restored
          : [{ from: "bot", text: introForAgent(slug, false), instant: true }]
      );
      setAskHistoryOpen(false);
      askStickToBottom.current = true;
    } catch {
      setAskMessages((m) => [
        ...m,
        { from: "bot", text: "Could not open that thread.", instant: true },
      ]);
    } finally {
      setAskThreadsLoading(false);
      focusInput(askInputRef);
    }
  };

  const switchAgent = (slug) => {
    setAskHistoryOpen(false);
    setAgentSlug(slug);
    setAskChatId(null);
    setAskMessages([
      {
        from: "bot",
        text: introForAgent(slug, true),
        instant: true,
      },
    ]);
  };

  const renderPanel = ({
    title,
    onClose,
    childrenHeader,
    listItems,
    listEndRef,
    listRef,
    stickToBottomRef,
    isLoading,
    formOnSubmit,
    formInputRef,
    formValue,
    formOnChange,
    placeholder,
    onBotChoice,
    onSaveStandards,
    onCancelStandards,
    onSaveQuestions,
    onCancelQuestions,
    onAddVisuals,
    onSkipVisuals,
    onFeedback,
    headerExtra,
    historyPanel,
  }) => {
    const minimized = panelMode === "min";
    const maximized = panelMode === "max";
    return (
    <div style={{ ...panel, ...(minimized ? panelMin : null), ...(maximized ? panelMax : null) }}>
      <div style={header}>
        <strong>{title}</strong>
        <div style={headerActions}>
          {headerExtra}
          <button
            type="button"
            style={iconBtn}
            title={minimized ? "Restore" : "Minimize"}
            aria-label={minimized ? "Restore" : "Minimize"}
            onClick={() => setPanelMode(minimized ? "default" : "min")}
          >
            {minimized ? "▢" : "–"}
          </button>
          <button
            type="button"
            style={iconBtn}
            title={maximized ? "Restore" : "Maximize"}
            aria-label={maximized ? "Restore" : "Maximize"}
            onClick={() => setPanelMode(maximized ? "default" : "max")}
          >
            {maximized ? "❐" : "□"}
          </button>
          <button type="button" style={iconBtn} title="Close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
      </div>
      {minimized ? null : (
        <>
      {childrenHeader}
      {historyPanel || (
      <div
        ref={listRef}
        style={list}
        onScroll={stickToBottomRef ? (event) => onListScroll(event, stickToBottomRef) : undefined}
      >
        {listItems.map((m, i) => (
          <div key={i} style={m.from === "user" ? userBubble : botBubbleWrap}>
            <div style={m.from === "user" ? undefined : botBubbleInner}>
              {m.from === "bot" ? (
                <WritingReply
                  text={m.text}
                  instant={Boolean(m.instant) || i !== listItems.length - 1}
                  stickToBottomRef={stickToBottomRef}
                  onChoice={onBotChoice}
                  onSaveStandards={onSaveStandards ? (payload) => onSaveStandards(i, payload) : undefined}
                  onCancelStandards={onCancelStandards ? () => onCancelStandards(i) : undefined}
                  onSaveQuestions={onSaveQuestions ? (payload) => onSaveQuestions(i, payload) : undefined}
                  onCancelQuestions={onCancelQuestions ? () => onCancelQuestions(i) : undefined}
                  onAddVisuals={onAddVisuals ? (payload) => onAddVisuals(i, payload) : undefined}
                  onSkipVisuals={onSkipVisuals ? () => onSkipVisuals(i) : undefined}
                />
              ) : (
                m.text
              )}
            </div>
            {m.from === "bot" && m.messageId && onFeedback ? (
              m.feedbackNoted || m.feedback ? (
                <div style={feedbackBlock}>
                  <div style={feedbackPrompt}>
                    {m.feedback === "unlike"
                      ? "Thanks for the feedback — we’ll use it to improve."
                      : "Thanks for the feedback — glad this helped."}
                  </div>
                </div>
              ) : (
                <div style={feedbackBlock}>
                  <div style={feedbackPrompt}>Was this response helpful?</div>
                  <div style={feedbackRow}>
                    <button
                      type="button"
                      style={feedbackBtn}
                      title="Like"
                      aria-label="Like"
                      onClick={() => onFeedback(i, "like")}
                    >
                      👍
                    </button>
                    <button
                      type="button"
                      style={feedbackBtn}
                      title="Unlike"
                      aria-label="Unlike"
                      onClick={() => onFeedback(i, "unlike")}
                    >
                      👎
                    </button>
                  </div>
                </div>
              )
            ) : null}
          </div>
        ))}
        {isLoading ? <GeneratingStatus /> : null}
        <div ref={listEndRef} />
      </div>
      )}
      {historyPanel ? null : (
      <form onSubmit={formOnSubmit} style={form}>
        <input
          ref={formInputRef}
          style={inputBox}
          value={formValue}
          onChange={formOnChange}
          placeholder={placeholder}
          autoFocus
        />
        <button
          type="submit"
          style={sendBtn}
          disabled={isLoading || !formValue.trim()}
          onMouseDown={(e) => e.preventDefault()}
        >
          Send
        </button>
      </form>
      )}
        </>
      )}
    </div>
    );
  };

  return (
    <>
      {askOpen || open ? (
        <div style={panelMode === "max" ? wrapFull : { ...wrap, bottom: 96 }}>
          {askOpen
            ? renderPanel({
                title: "Ask AI",
                onClose: () => {
                  setAskOpen(false);
                  setAskHistoryOpen(false);
                  setPanelMode("default");
                },
                headerExtra: (
                  <>
                    <button
                      type="button"
                      style={headerTextBtn}
                      title="Start a new thread"
                      onClick={startNewAskThread}
                    >
                      New
                    </button>
                    <button
                      type="button"
                      style={headerTextBtn}
                      title={askHistoryOpen ? "Back to this thread" : "Thread history"}
                      onClick={() => (askHistoryOpen ? setAskHistoryOpen(false) : openAskHistory())}
                    >
                      {askHistoryOpen ? "Back" : "History"}
                    </button>
                  </>
                ),
                historyPanel: askHistoryOpen ? (
                  <div style={historyList}>
                    <button type="button" style={newThreadRow} onClick={startNewAskThread}>
                      <strong>Start new thread</strong>
                      <span style={threadMeta}>
                        {AGENTS.find((item) => item.slug === agentSlug)?.name || "This agent"}
                      </span>
                    </button>
                    {askThreadsLoading ? (
                      <div style={threadEmpty}>Loading threads...</div>
                    ) : askThreads.length ? (
                      askThreads.map((thread) => (
                        <button
                          key={thread.chatId}
                          type="button"
                          style={
                            thread.chatId === askChatId
                              ? { ...threadRow, ...threadRowActive }
                              : threadRow
                          }
                          onClick={() => openAskThread(thread.chatId)}
                        >
                          <strong style={threadTitle}>{thread.title || "Thread"}</strong>
                          <span style={threadMeta}>
                            {thread.messageCount || 0} messages
                            {thread.updatedAt
                              ? ` · ${new Date(thread.updatedAt).toLocaleString()}`
                              : ""}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div style={threadEmpty}>No earlier threads for this agent.</div>
                    )}
                  </div>
                ) : null,
                childrenHeader: (
                  <div style={agentRow}>
                    {AGENTS.map((agent) => (
                      <button
                        key={agent.slug}
                        type="button"
                        style={agentSlug === agent.slug ? agentChipActive : agentChip}
                        onClick={() => switchAgent(agent.slug)}
                      >
                        {agent.name.replace(" Agent", "")}
                      </button>
                    ))}
                  </div>
                ),
                listItems: askMessages,
                listEndRef: askEndRef,
                listRef: askListRef,
                stickToBottomRef: askStickToBottom,
                isLoading: askLoading,
                formOnSubmit: sendAskAi,
                formInputRef: askInputRef,
                formValue: askInput,
                formOnChange: (e) => setAskInput(e.target.value),
                placeholder: "Ask the selected agent...",
                onBotChoice: sendAskAiMessage,
                onSaveStandards: saveAskStandards,
                onCancelStandards: cancelAskStandards,
                onSaveQuestions: saveAskQuestions,
                onCancelQuestions: cancelAskQuestions,
                onAddVisuals: addAskVisuals,
                onSkipVisuals: skipAskVisuals,
                onFeedback: setAskFeedback,
              })
            : null}

          {open
            ? renderPanel({
                title: "Chat",
                onClose: () => {
                  setOpen(false);
                  setPanelMode("default");
                },
                listItems: messages,
                listEndRef: endRef,
                listRef: chatListRef,
                stickToBottomRef: chatStickToBottom,
                isLoading: loading,
                formOnSubmit: send,
                formInputRef: inputRef,
                formValue: input,
                formOnChange: (e) => setInput(e.target.value),
                placeholder: "Type a message...",
              })
            : null}
        </div>
      ) : null}

      <div style={{ ...wrap, bottom }}>
        <div style={fabStack}>
          {!open && !askOpen && showAskAi ? (
            <button
              type="button"
              style={fab}
              onClick={() => {
                setOpen(false);
                setAskOpen(true);
                setPanelMode("default");
              }}
            >
              Ask AI
            </button>
          ) : null}
          <button type="button" style={fab} onClick={openHelp}>
            <span style={helpIconWrap} aria-hidden="true">
              ?
            </span>
            Help
          </button>
        </div>
      </div>
    </>
  );
}

const wrap = {
  position: "fixed",
  right: 16,
  bottom: 16,
  zIndex: 1050,
};

const fabStack = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-end",
  gap: 10,
};

const fab = {
  border: 0,
  borderRadius: 999,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  height: 40,
  padding: "0 16px",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  boxShadow: "0 4px 8px rgba(0,0,0,.12), 0 0 2px rgba(0,0,0,.12)",
  cursor: "pointer",
};

const helpIconWrap = {
  width: 16,
  height: 16,
  border: "2px solid #fff",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
};

const panel = {
  width: 420,
  maxWidth: "calc(100vw - 24px)",
  height: 520,
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 8px 28px rgba(0,0,0,.2)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const header = {
  background: "rgb(142, 68, 173)",
  color: "#fff",
  padding: "10px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const headerActions = {
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const headerTextBtn = {
  border: 0,
  background: "transparent",
  color: "#fff",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
};

const historyList = {
  flex: 1,
  overflowY: "auto",
  padding: 12,
  background: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const threadRow = {
  border: "1px solid #e2e8f0",
  background: "#fff",
  borderRadius: 10,
  padding: "10px 12px",
  textAlign: "left",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const threadRowActive = {
  borderColor: "rgb(142, 68, 173)",
  background: "rgba(142, 68, 173, 0.08)",
};

const newThreadRow = {
  ...threadRow,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  border: 0,
};

const threadTitle = {
  fontSize: 14,
  fontWeight: 700,
};

const threadMeta = {
  fontSize: 12,
  opacity: 0.8,
};

const threadEmpty = {
  color: "#64748b",
  fontSize: 13,
  padding: "8px 4px",
};

const iconBtn = {
  border: 0,
  background: "transparent",
  color: "#fff",
  fontSize: 18,
  lineHeight: 1,
  cursor: "pointer",
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
};

const wrapFull = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
};

const panelMin = {
  height: "auto",
  width: 280,
};

const panelMax = {
  width: "100vw",
  height: "100vh",
  maxWidth: "100vw",
  borderRadius: 0,
  boxShadow: "none",
};

const agentRow = {
  display: "flex",
  gap: 6,
  padding: "8px 10px",
  background: "#f8fafc",
  flexWrap: "wrap",
};

const agentChip = {
  border: 0,
  background: "rgba(142, 68, 173, 0.16)",
  color: "#9ca3af",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

const agentChipActive = {
  ...agentChip,
  color: "#000",
};

const list = {
  flex: 1,
  overflowY: "auto",
  padding: 12,
  background: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const botBubble = {
  alignSelf: "flex-start",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "8px 10px",
  maxWidth: "92%",
  fontSize: 14,
  whiteSpace: "normal",
};

const botBubbleWrap = {
  ...botBubble,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  paddingBottom: 6,
};

const botBubbleInner = {
  width: "100%",
};

const feedbackBlock = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  marginTop: 4,
  paddingTop: 6,
  borderTop: "1px solid #eef2f7",
};

const feedbackPrompt = {
  fontSize: 12,
  color: "#64748b",
  lineHeight: 1.35,
};

const feedbackRow = {
  display: "flex",
  gap: 6,
  alignItems: "center",
};

const feedbackBtn = {
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  borderRadius: 8,
  width: 32,
  height: 28,
  padding: 0,
  cursor: "pointer",
  fontSize: 13,
  lineHeight: 1,
};

const feedbackBtnActive = {
  ...feedbackBtn,
  background: "rgba(142, 68, 173, 0.12)",
  borderColor: "rgb(142, 68, 173)",
};

const userBubble = {
  ...botBubble,
  alignSelf: "flex-end",
  background: "rgb(142, 68, 173)",
  color: "#fff",
  border: 0,
  whiteSpace: "pre-wrap",
};

const form = {
  display: "flex",
  gap: 8,
  padding: 10,
  borderTop: "1px solid #e2e8f0",
};

const inputBox = {
  flex: 1,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 14,
};

const sendBtn = {
  border: 0,
  borderRadius: 8,
  background: "rgb(142, 68, 173)",
  color: "#fff",
  fontWeight: 600,
  padding: "8px 12px",
  cursor: "pointer",
};
